import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "sonner";
import { useTerminalPinQuery } from "Api/Queries/UserSettings/useTerminalPinQuery";
import { useRegenerateTerminalPinMutation } from "Api/Mutations/UserSettings/useRegenerateTerminalPinMutation";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.TerminalPin;

export const TerminalPinSection = () => {
  const { t } = useResource();
  const { data, isLoading } = useTerminalPinQuery();
  const [isRevealed, setIsRevealed] = useState(false);

  const { mutate: regenerate, isPending: isRegenerating } =
    useRegenerateTerminalPinMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.RegenerateSuccess));
        setIsRevealed(true);
      },
      onError: () => toast.error(t(RESOURCES.RegenerateError)),
    });

  const handleRegenerate = () => {
    if (window.confirm(t(RESOURCES.RegenerateConfirm))) {
      regenerate();
    }
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  const pin = data?.pin ?? null;

  return (
    <Stack gap={2}>
      <Typography variant="h6">{t(RESOURCES.Title)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.Description)}
      </Typography>

      {pin === null ? (
        <Alert severity="info">{t(RESOURCES.NoPinYet)}</Alert>
      ) : (
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Box
            px={2}
            py={1}
            sx={{
              borderRadius: 1,
              backgroundColor: "action.hover",
              minWidth: 110,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{ fontFamily: "monospace", fontSize: 24, letterSpacing: 4 }}
            >
              {isRevealed ? pin : "••••"}
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={isRevealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setIsRevealed((prev) => !prev)}
          >
            {t(isRevealed ? RESOURCES.Hide : RESOURCES.Reveal)}
          </Button>
        </Stack>
      )}

      <Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRegenerate}
          disabled={isRegenerating}
        >
          {t(RESOURCES.Regenerate)}
        </Button>
      </Box>
    </Stack>
  );
};
